import React, { useState, useEffect } from 'react';
import { FaDownload, FaSearch, FaCalendarAlt, FaUserCheck, FaUserTimes, FaExclamationTriangle, FaMoneyBillWave, FaHandHoldingUsd, FaBalanceScale, FaClock, FaInfoCircle, FaLightbulb, FaStar, FaTrophy } from 'react-icons/fa';
import { FiArrowUp, FiArrowDown } from 'react-icons/fi';
import useAxios from '../utils/useAxios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useSidebar } from '../context/SidebarContext';
import OrganisationSelector from '../components/OrganisationSelector';
import styles from '../styles/RHStatsDetailPage';
import { useAuth } from '../context/AuthContext';
import Swal from 'sweetalert2';

const formatCurrency = (amount) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(amount || 0);
const formatMoney = (amount) => `${(amount || 0).toLocaleString('fr-FR')} FCFA`;

const RHStatsDetailPage = () => {
  const [stats, setStats] = useState([]);
  const [pagination, setPagination] = useState({ totalPages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const axiosInstance = useAxios();
  const { sidebarVisible } = useSidebar();

  // États pour la gestion des organisations
  const [selectedOrganisation, setSelectedOrganisation] = useState('');
  const [currentOrganisation, setCurrentOrganisation] = useState(null);
  const { user } = useAuth();

  // États pour les primes d'innovation
  const [primesInnovation, setPrimesInnovation] = useState([]);
  const [loadingPrimes, setLoadingPrimes] = useState(false);
  const [showPrimeDetails, setShowPrimeDetails] = useState(false);
  const [selectedPrime, setSelectedPrime] = useState(null);

  const fetchOrganisations = async () => {
    try {
      const response = await axiosInstance.get('/organisations/');
      if (response.data.length > 0) {
        const firstOrg = response.data[0];
        setSelectedOrganisation(firstOrg.id);
        setCurrentOrganisation(firstOrg);
      }
    } catch (err) {
      console.error("Erreur lors de la récupération des organisations", err);
    }
  };

  const handleOrganisationChange = (orgId, org) => {
    setSelectedOrganisation(orgId);
    setCurrentOrganisation(org);
    fetchStats(1, orgId);
    fetchPrimesInnovation(orgId);
  };

  const fetchStats = async (targetPage = 1, orgId = selectedOrganisation) => {
    if (!orgId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: targetPage,
        search,
        date_debut: dateDebut,
        date_fin: dateFin,
        organisation: orgId,
        page_size: pageSize === 'all' ? totalCount : pageSize
      });
      const response = await axiosInstance.get(`/pointage/employes/stats/?${params}`);
      setStats(response.data.results);
      setTotalCount(response.data.count);
      if (pageSize === 'all') {
        setPagination({ totalPages: 1 });
      } else {
        setPagination({ totalPages: Math.ceil(response.data.count / pageSize) });
      }
      setPage(targetPage);
    } catch (err) {
      console.error("Erreur lors de la récupération des statistiques", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPrimesInnovation = async (orgId) => {
    if (!orgId) return;
    setLoadingPrimes(true);
    try {
      const params = new URLSearchParams({
        organisation: orgId,
        ...(dateDebut && { date_debut: dateDebut }),
        ...(dateFin && { date_fin: dateFin }),
      });
      const response = await axiosInstance.get('/innovation/innovations/historique-primes/', { params });
      setPrimesInnovation(response.data.results || []);
    } catch (err) {
      console.error("Erreur lors de la récupération des primes d'innovation", err);
      setPrimesInnovation([]);
    } finally {
      setLoadingPrimes(false);
    }
  };

  const generatePageSizeOptions = () => {
    const options = [10, 20, 30, 50];
    if (totalCount > 50 && !options.includes(totalCount)) {
      options.push(totalCount);
    }
    options.push('all');
    return options;
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) fetchStats(newPage);
  };

  const getSoldeStyle = (solde) => (solde > 0 ? styles.positiveSolde : solde < 0 ? styles.negativeSolde : styles.zeroSolde);

  const fetchAllStats = async () => {
    try {
      let allStats = [];
      let currentPage = 1;
      let hasMore = true;
      while (hasMore) {
        const params = new URLSearchParams({
          page: currentPage,
          search,
          date_debut: dateDebut,
          date_fin: dateFin,
          organisation: selectedOrganisation,
          page_size: 50
        });
        const response = await axiosInstance.get(`/pointage/employes/stats/?${params}`);
        allStats = [...allStats, ...response.data.results];
        hasMore = currentPage * 50 < response.data.count;
        currentPage++;
      }
      return allStats;
    } catch (err) {
      console.error("Erreur lors de la récupération de toutes les statistiques", err);
      return [];
    }
  };

  const fetchAllPrimesInnovation = async () => {
    try {
      const params = new URLSearchParams({
        organisation: selectedOrganisation,
        ...(dateDebut && { date_debut: dateDebut }),
        ...(dateFin && { date_fin: dateFin }),
      });
      const response = await axiosInstance.get('/innovation/innovations/historique-primes/', { params });
      return response.data.results || [];
    } catch (err) {
      console.error("Erreur lors de la récupération des primes d'innovation", err);
      return [];
    }
  };
  
const formatNum = (n) => {
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const handleExportPDF = async () => {
  setLoading(true);
  try {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 14;
    const marginY = 20;
    let currentY = marginY;

    // ==================== FORMATEUR DE NOMBRES ====================
    const formatNum = (n) => {
      return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    };

    // Récupération des données
    const allStats = await fetchAllStats();
    const allPrimesInnovation = await fetchAllPrimesInnovation();

    // Calcul des totaux
    const totalPresences = allStats.reduce((sum, s) => sum + s.nbr_presences, 0);
    const totalRetards = allStats.reduce((sum, s) => sum + s.nbr_retards, 0);
    const totalAbsences = allStats.reduce((sum, s) => sum + s.nbr_absences, 0);
    const totalHeuresSup = allStats.reduce((sum, s) => sum + (s.heures_supplementaires || 0), 0);
    const totalHeuresTravaillees = allStats.reduce((sum, s) => sum + (s.heures_travaillees || 0), 0);
    const totalPrimePonctualite = allStats.reduce((sum, s) => sum + s.montant_primes, 0);
    const totalPrimeHeuresSup = allStats.reduce((sum, s) => sum + (s.montant_heures_supplementaires || 0), 0);
    const totalPrimeInnovation = allPrimesInnovation.reduce((sum, p) => sum + (p.montant_total || 0), 0);
    const totalPenalites = allStats.reduce((sum, s) => sum + s.montant_penalites, 0);
    const totalPrimesGlobal = totalPrimePonctualite + totalPrimeHeuresSup + totalPrimeInnovation;
    const totalSolde = totalPrimesGlobal - totalPenalites;

    // ==================== FONCTIONS UTILITAIRES ====================
    const addHeader = (y) => {
      doc.setFontSize(22);
      doc.setTextColor(27, 1, 155);
      doc.setFont('helvetica', 'bold');
      doc.text('RAPPORT STATISTIQUES RH', pageWidth / 2, y, { align: 'center' });

      doc.setFontSize(12);
      doc.setTextColor(80, 80, 80);
      doc.setFont('helvetica', 'normal');
      doc.text(currentOrganisation?.nom || '', pageWidth / 2, y + 7, { align: 'center' });

      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      const periodeText = dateDebut && dateFin ? `Periode: ${dateDebut} au ${dateFin}` : "Periode: Mois en cours";
      doc.text(periodeText, pageWidth / 2, y + 12, { align: 'center' });
      doc.text(`Genere le: ${new Date().toLocaleDateString('fr-FR')} a ${new Date().toLocaleTimeString('fr-FR')}`, pageWidth / 2, y + 17, { align: 'center' });

      return y + 25;
    };

    const addSectionTitle = (title, y, color = [27, 1, 155]) => {
      doc.setFontSize(14);
      doc.setTextColor(color[0], color[1], color[2]);
      doc.setFont('helvetica', 'bold');
      doc.text(title, marginX, y);
      doc.setDrawColor(color[0], color[1], color[2]);
      doc.setLineWidth(0.8);
      doc.line(marginX, y + 2, pageWidth - marginX, y + 2);
      return y + 10;
    };

    const addFooter = (pageNum, totalPages) => {
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.setFont('helvetica', 'italic');
      doc.text(
        `Document genere automatiquement - Page ${pageNum} sur ${totalPages}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    };

    // ==================== PAGE 1 - RECAPITULATIF GENERAL ====================
    let yPos = addHeader(currentY);

    yPos = addSectionTitle('RECAPITULATIF GENERAL', yPos);
    yPos += 5;

    const recapData = [
      ['INDICATEURS DE PRESENCE', 'VALEURS'],
      ['Presences', `${totalPresences}`],
      ['Retards', `${totalRetards}`],
      ['Absences', `${totalAbsences}`],
      ['Heures travaillees', `${totalHeuresTravaillees.toFixed(2)} h`],
      ['Heures supplementaires', `${totalHeuresSup.toFixed(2)} h`],
    ];

    autoTable(doc, {
      startY: yPos,
      body: recapData,
      theme: 'plain',
      styles: { fontSize: 10, cellPadding: 5, lineColor: [220, 220, 220], lineWidth: 0.2 },
      columnStyles: {
        0: { cellWidth: 70, fontStyle: 'bold', textColor: [50, 50, 50], fillColor: [248, 248, 248] },
        1: { cellWidth: 60, halign: 'right', fontStyle: 'normal' },
      },
      didDrawCell: (data) => {
        if (data.row.raw[0] === 'INDICATEURS DE PRESENCE') {
          data.cell.styles.fillColor = [27, 1, 155];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });

    yPos = doc.lastAutoTable.finalY + 10;

    // Section Financiere
    yPos = addSectionTitle('RECAPITULATIF FINANCIER', yPos, [34, 197, 94]);
    yPos += 5;

    const financialRecapData = [
      ['PRIMES', 'MONTANT'],
      ['Prime ponctualite', `${formatNum(totalPrimePonctualite)} FCFA`],
      ['Prime heures supplementaires', `${formatNum(totalPrimeHeuresSup)} FCFA`],
      ['Prime innovation', `${formatNum(totalPrimeInnovation)} FCFA`],
      ['TOTAL PRIMES', `${formatNum(totalPrimesGlobal)} FCFA`],
      ['Penalites', `${formatNum(totalPenalites)} FCFA`],
      ['SOLDE NET', `${formatNum(totalSolde)} FCFA`],
    ];

    autoTable(doc, {
      startY: yPos,
      body: financialRecapData,
      theme: 'plain',
      styles: { fontSize: 10, cellPadding: 5, lineColor: [220, 220, 220], lineWidth: 0.2 },
      columnStyles: {
        0: { cellWidth: 70, fontStyle: 'bold', fillColor: [248, 248, 248] },
        1: { cellWidth: 60, halign: 'right' },
      },
      didDrawCell: (data) => {
        if (data.row.raw[0] === 'PRIMES') {
          data.cell.styles.fillColor = [34, 197, 94];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fontStyle = 'bold';
        }
        if (data.row.raw[0] === 'TOTAL PRIMES') {
          data.cell.styles.textColor = [34, 197, 94];
          data.cell.styles.fontStyle = 'bold';
        }
        if (data.row.raw[0] === 'SOLDE NET') {
          data.cell.styles.textColor = totalSolde >= 0 ? [34, 197, 94] : [220, 38, 38];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 11;
        }
      },
    });

    yPos = doc.lastAutoTable.finalY + 15;

    // ==================== PAGE 2 - TABLEAU FINANCIER DETAILLE ====================
    if (yPos + 60 > pageHeight - 30) {
      doc.addPage();
      yPos = marginY;
      addFooter(doc.internal.getNumberOfPages(), doc.internal.getNumberOfPages());
    }

    yPos = addSectionTitle('TABLEAU FINANCIER PAR EMPLOYE', yPos, [34, 197, 94]);
    yPos += 5;

    const financialColumns = [
      'Employe',
      'Prim. Ponct.',
      'Prim. Hres Sup',
      'Prim. Innov.',
      'Total Primes',
      'Penalites',
      'Solde Net'
    ];

    const financialRows = allStats.map(s => {
      const primesInnovationEmploye = allPrimesInnovation
        .filter(p => p.auteur_nom === s.nom && p.auteur_prenom === s.prenom)
        .reduce((sum, p) => sum + p.montant_total, 0);
      const totalPrimes = s.montant_primes + (s.montant_heures_supplementaires || 0) + primesInnovationEmploye;
      const solde = totalPrimes - s.montant_penalites;

      return [
        `${s.nom} ${s.prenom}`,
        `${formatNum(s.montant_primes)}`,
        `${formatNum(s.montant_heures_supplementaires || 0)}`,
        `${formatNum(primesInnovationEmploye)}`,
        `${formatNum(totalPrimes)}`,
        `${formatNum(s.montant_penalites)}`,
        `${formatNum(solde)}`,
      ];
    });

    // Ajout du total général
    financialRows.push([
      'TOTAL GENERAL',
      `${formatNum(totalPrimePonctualite)}`,
      `${formatNum(totalPrimeHeuresSup)}`,
      `${formatNum(totalPrimeInnovation)}`,
      `${formatNum(totalPrimesGlobal)}`,
      `${formatNum(totalPenalites)}`,
      `${formatNum(totalSolde)}`,
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [financialColumns],
      body: financialRows,
      theme: 'striped',
      headStyles: {
        fillColor: [34, 197, 94],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 7,
        halign: 'center',
      },
      styles: { fontSize: 7, cellPadding: 2, halign: 'right' },
      columnStyles: {
        0: { halign: 'left', cellWidth: 42 },  // Employe
        1: { cellWidth: 24 },                   // Prim. Ponct.
        2: { cellWidth: 24 },                   // Prim. Hres Sup
        3: { cellWidth: 24 },                   // Prim. Innov.
        4: { cellWidth: 24 },                   // Total Primes
        5: { cellWidth: 22 },                   // Penalites
        6: { cellWidth: 22 },                   // Solde Net
        // Total: 42+24+24+24+24+22+22 = 182 mm (= 210 - 14*2) ✓
      },
      didParseCell: (data) => {
        if (data.row.index === financialRows.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [245, 245, 245];
        }
        if (data.column.index === 6 && data.row.index !== financialRows.length - 1) {
          const soldeValue = parseFloat(data.cell.text[0].replace(/\s/g, ''));
          if (soldeValue >= 0) {
            data.cell.styles.textColor = [34, 197, 94];
          } else {
            data.cell.styles.textColor = [220, 38, 38];
          }
        }
      },
    });

    yPos = doc.lastAutoTable.finalY + 15;

    // ==================== PAGE 3 - TABLEAU DES PRESENCES ====================
    if (yPos + 50 > pageHeight - 30) {
      doc.addPage();
      yPos = marginY;
      addFooter(doc.internal.getNumberOfPages(), doc.internal.getNumberOfPages());
    }

    yPos = addSectionTitle('TABLEAU DES PRESENCES ET HEURES', yPos, [59, 130, 246]);
    yPos += 5;

    const attendanceColumns = [
      'Employe',
      'Presences',
      'Retards',
      'Absences',
      'Heures Travaillees',
      'Heures Supplementaires'
    ];

    const attendanceRows = allStats.map(s => [
      `${s.nom} ${s.prenom}`,
      s.nbr_presences.toString(),
      s.nbr_retards.toString(),
      s.nbr_absences.toString(),
      `${(s.heures_travaillees || 0).toFixed(2)} h`,
      `${(s.heures_supplementaires || 0).toFixed(2)} h`,
    ]);

    attendanceRows.push([
      'TOTAL',
      totalPresences.toString(),
      totalRetards.toString(),
      totalAbsences.toString(),
      `${totalHeuresTravaillees.toFixed(2)} h`,
      `${totalHeuresSup.toFixed(2)} h`,
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [attendanceColumns],
      body: attendanceRows,
      theme: 'striped',
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
        halign: 'center',
      },
      styles: { fontSize: 8, cellPadding: 3, halign: 'center' },
      columnStyles: {
        0: { halign: 'left', cellWidth: 45 },
        1: { cellWidth: 22 },
        2: { cellWidth: 22 },
        3: { cellWidth: 22 },
        4: { cellWidth: 38 },
        5: { cellWidth: 38 },
        // Total: 45+22+22+22+38+38 = 187 mm — leger debordement possible
        // si ca deborde, reduire le 0 a 40 => 182 mm ✓
      },
      didParseCell: (data) => {
        if (data.row.index === attendanceRows.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [245, 245, 245];
        }
      },
    });

    yPos = doc.lastAutoTable.finalY + 15;

    // ==================== PAGE 4 - PRIMES D'INNOVATION ====================
    if (allPrimesInnovation.length > 0) {
      if (yPos + 50 > pageHeight - 30) {
        doc.addPage();
        yPos = marginY;
        addFooter(doc.internal.getNumberOfPages(), doc.internal.getNumberOfPages());
      }

      yPos = addSectionTitle('PRIMES INNOVATION ATTRIBUEES', yPos, [245, 158, 11]);
      yPos += 5;

      const innovationColumns = ['Innovation', 'Beneficiaire', 'Score', 'Montant', "Date d'attribution"];
      const innovationRows = allPrimesInnovation.map(p => [
        p.innovation_titre,
        `${p.auteur_prenom} ${p.auteur_nom}`,
        `${p.score_final} pts`,
        `${formatNum(p.montant_total)} FCFA`,
        p.date_attribution,
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [innovationColumns],
        body: innovationRows,
        theme: 'striped',
        headStyles: {
          fillColor: [245, 158, 11],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 9,
          halign: 'center',
        },
        styles: { fontSize: 8, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 60, halign: 'left' },
          1: { cellWidth: 40, halign: 'left' },
          2: { cellWidth: 25, halign: 'center' },
          3: { cellWidth: 35, halign: 'right' },
          4: { cellWidth: 22, halign: 'center' },
          // Total: 60+40+25+35+22 = 182 mm ✓
        },
        didParseCell: (data) => {
          if (data.column.index === 3 && data.section === 'body') {
            data.cell.styles.textColor = [34, 197, 94];
          }
        },
      });
    }

    // ==================== PIEDS DE PAGE ====================
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      addFooter(i, pageCount);
    }

    doc.save(`rapport_statistiques_${new Date().toISOString().split('T')[0]}_${currentOrganisation?.nom || ''}.pdf`);

    Swal.fire({
      icon: 'success',
      title: 'PDF genere avec succes',
      text: 'Le rapport a ete telecharge.',
      timer: 2000,
      showConfirmButton: false,
    });
  } catch (err) {
    console.error("Erreur lors de la generation du PDF", err);
    Swal.fire({
      icon: 'error',
      title: 'Erreur',
      text: 'Une erreur est survenue lors de la generation du PDF.'
    });
  } finally {
    setLoading(false);
  }
};

  // Calculs pour les totaux
  const totalPrimePonctualite = stats.reduce((sum, s) => sum + s.montant_primes, 0);
  const totalPrimeHeuresSup = stats.reduce((sum, s) => sum + (s.montant_heures_supplementaires || 0), 0);
  const totalPrimeInnovation = primesInnovation.reduce((sum, p) => sum + (p.montant_total || 0), 0);
  const totalPrimesGlobal = totalPrimePonctualite + totalPrimeHeuresSup + totalPrimeInnovation;
  const totalPenalites = stats.reduce((sum, s) => sum + s.montant_penalites, 0);
  const totalHeuresSupHeures = stats.reduce((sum, s) => sum + (s.heures_supplementaires || 0), 0);
  const soldeGlobal = totalPrimesGlobal - totalPenalites;

  useEffect(() => {
    fetchOrganisations();
  }, []);

  useEffect(() => {
    if (selectedOrganisation) {
      fetchStats(1);
      fetchPrimesInnovation(selectedOrganisation);
    }
  }, [search, dateDebut, dateFin, pageSize, selectedOrganisation]);

  // Modal pour les détails des primes d'innovation
  const PrimeDetailsModal = ({ show, onClose, prime }) => {
    if (!show || !prime) return null;

    return (
      <div style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
      }} onClick={onClose}>
        <div style={{
          backgroundColor: 'white', borderRadius: '16px', padding: '24px',
          width: '95%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto'
        }} onClick={e => e.stopPropagation()}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            borderBottom: '2px solid #1B019B', paddingBottom: '12px', marginBottom: '20px'
          }}>
            <h3 style={{ margin: 0, color: '#1B019B' }}>Détail de la prime d'innovation</h3>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>✕</button>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <div style={{ backgroundColor: '#fef3c7', borderRadius: '12px', padding: '16px', marginBottom: '16px', textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color: '#b45309', marginBottom: '8px' }}>Montant de la prime</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#d97706' }}>{formatCurrency(prime.montant_total)}</div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div style={{ backgroundColor: '#f3f4f6', borderRadius: '8px', padding: '12px' }}>
                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>Innovation</div>
                <div style={{ fontWeight: '600', fontSize: '14px' }}>{prime.innovation_titre}</div>
              </div>
              <div style={{ backgroundColor: '#f3f4f6', borderRadius: '8px', padding: '12px' }}>
                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>Bénéficiaire</div>
                <div style={{ fontWeight: '600', fontSize: '14px' }}>{prime.auteur_prenom} {prime.auteur_nom}</div>
              </div>
              <div style={{ backgroundColor: '#f3f4f6', borderRadius: '8px', padding: '12px' }}>
                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>Score final</div>
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#1B019B' }}>{prime.score_final} pts</div>
              </div>
              <div style={{ backgroundColor: '#f3f4f6', borderRadius: '8px', padding: '12px' }}>
                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>Date d'attribution</div>
                <div style={{ fontWeight: '600', fontSize: '14px' }}>{prime.date_attribution}</div>
              </div>
            </div>

            {prime.regle_appliquee && (
              <div style={{ backgroundColor: '#ede9fe', borderRadius: '8px', padding: '12px' }}>
                <div style={{ fontSize: '12px', color: '#1B019B', marginBottom: '8px', fontWeight: '600' }}>Règle appliquée</div>
                <div style={{ fontSize: '13px', color: '#4b5563' }}>
                  Score {prime.regle_appliquee.score_min} – {prime.regle_appliquee.score_max} → Prime de {formatCurrency(prime.regle_appliquee.montant)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ ...styles.container, marginLeft: sidebarVisible ? 250 : 0 }}>
      <div className="text-center mb-3">
        <h3 style={styles.header}>Statistiques des Employés</h3>
        <hr style={styles.hr} />
      </div>

      <div style={styles.organisationHeader}>
        <OrganisationSelector
          onOrganisationChange={handleOrganisationChange}
          selectedOrganisation={selectedOrganisation}
        />
        {currentOrganisation && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: '#666', fontSize: '14px' }}>
              Statistiques des employés pour cette organisation
            </span>
          </div>
        )}
      </div>

      {currentOrganisation ? (
        <>
          <h1 style={{ color: "#1B019B", fontSize: "28px", fontWeight: "bold", marginBottom: "20px" }}>
            📊 STATISTIQUES - {currentOrganisation.nom}
          </h1>

          {/* Information supplémentaire sur les primes */}
          <div style={{
            backgroundColor: '#f8f9fa',
            borderRadius: '10px',
            padding: '15px 20px',
            marginBottom: '25px',
            border: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '25px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FaInfoCircle style={{ color: '#1B019B', fontSize: '20px' }} />
              <span style={{ fontWeight: '600', color: '#1B019B' }}>Détail des primes :</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaUserCheck style={{ color: '#28a745' }} />
                <span style={{ color: '#28a745', fontWeight: '600' }}>Ponctualité:</span>
                <span style={{ fontWeight: '500' }}>{formatMoney(totalPrimePonctualite)}</span>
              </div>

              <div style={{ width: '1px', height: '25px', backgroundColor: '#ddd' }}></div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaClock style={{ color: '#0288d1' }} />
                <span style={{ color: '#0288d1', fontWeight: '600' }}>Heures sup:</span>
                <span style={{ fontWeight: '500' }}>{formatMoney(totalPrimeHeuresSup)}</span>
                <span style={{ color: '#666', fontSize: '0.9rem' }}>({totalHeuresSupHeures}h)</span>
              </div>

              <div style={{ width: '1px', height: '25px', backgroundColor: '#ddd' }}></div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaLightbulb style={{ color: '#f59e0b' }} />
                <span style={{ color: '#f59e0b', fontWeight: '600' }}>Innovation:</span>
                <span style={{ fontWeight: '500' }}>{formatMoney(totalPrimeInnovation)}</span>
              </div>

              <div style={{ width: '1px', height: '25px', backgroundColor: '#ddd' }}></div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaTrophy style={{ color: '#1B019B' }} />
                <span style={{ fontWeight: '600' }}>Total primes:</span>
                <span style={{ fontWeight: '700', color: '#28a745' }}>{formatMoney(totalPrimesGlobal)}</span>
              </div>
            </div>
          </div>

          {/* Cartes principales */}
          <div className="row mb-4">
            <div className="col-md-4 mb-3">
              <div className="stats-card positive" style={{ ...styles.card, ...styles.positiveCard }}>
                <FaMoneyBillWave size={28} />
                <h5 className="mt-2" style={{ fontWeight: '700' }}>{formatMoney(totalPrimesGlobal)}</h5>
                <p className="mb-0">Total Primes (Ponctualité + Heures Sup + Innovation)</p>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <div className="stats-card negative" style={{ ...styles.card, ...styles.negativeCard }}>
                <FaHandHoldingUsd size={28} />
                <h5 className="mt-2" style={{ fontWeight: '700' }}>{formatMoney(totalPenalites)}</h5>
                <p className="mb-0">Total Pénalités</p>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <div className="stats-card" style={{ ...styles.card, ...styles.negativeCard }}>
                <FaBalanceScale size={32} />
                <h5 className="mt-2" style={{ fontWeight: '700', fontSize: '1.3rem' }}>{formatMoney(Math.abs(soldeGlobal))}</h5>
                <p className="mb-0">{soldeGlobal >= 0 ? 'Rémunération Nette' : 'Sanctions Net'}</p>
              </div>
            </div>
          </div>

          {/* Section des primes d'innovation */}
          {primesInnovation.length > 0 && (
            <div style={{ marginBottom: '25px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                <FaLightbulb style={{ color: '#f59e0b', fontSize: '20px' }} />
                <h4 style={{ margin: 0, color: '#1B019B', fontSize: '18px', fontWeight: '600' }}>
                  Primes d'innovation récentes
                </h4>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f59e0b', color: 'white' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left' }}>Innovation</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left' }}>Bénéficiaire</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left' }}>Score</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left' }}>Prime</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left' }}>Date</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {primesInnovation.slice(0, 5).map((prime, idx) => (
                      <tr key={prime.id} style={{ borderBottom: idx < 4 ? '1px solid #e5e7eb' : 'none' }}>
                        <td style={{ padding: '10px 12px', fontWeight: '500' }}>{prime.innovation_titre}</td>
                        <td style={{ padding: '10px 12px' }}>{prime.auteur_prenom} {prime.auteur_nom}</td>
                        <td style={{ padding: '10px 12px' }}><strong style={{ color: '#f59e0b' }}>{prime.score_final}</strong> pts</td>
                        <td style={{ padding: '10px 12px', color: '#28a745', fontWeight: '600' }}>{formatCurrency(prime.montant_total)}</td>
                        <td style={{ padding: '10px 12px' }}>{prime.date_attribution}</td>
                        <td style={{ padding: '10px 12px' }}>
                          <button
                            onClick={() => { setSelectedPrime(prime); setShowPrimeDetails(true); }}
                            style={{
                              backgroundColor: '#1B019B', color: 'white', border: 'none',
                              padding: '4px 10px', borderRadius: '5px', cursor: 'pointer',
                              fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px'
                            }}
                          >
                            <FaInfoCircle size={12} /> Détail
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Barre de recherche et filtres */}
          <div className="d-flex flex-wrap align-items-center mb-4" style={{ gap: '15px' }}>
            <div className="d-flex align-items-center">
              <FaSearch className="me-2" style={{ color: '#1B019B' }} />
              <input
                type="text"
                placeholder="Rechercher nom, prénom..."
                style={styles.searchBar}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="d-flex align-items-center">
              <FaCalendarAlt className="me-2" style={{ color: '#1B019B' }} />
              <input
                type="date"
                value={dateDebut}
                onChange={(e) => setDateDebut(e.target.value)}
                style={styles.dateInput}
              />
            </div>

            <div className="d-flex align-items-center">
              <span className="me-2" style={{ color: '#1B019B' }}>à</span>
              <input
                type="date"
                value={dateFin}
                onChange={(e) => setDateFin(e.target.value)}
                style={styles.dateInput}
              />
            </div>

            <div>
              <label style={{ color: '#1B019B', fontWeight: 500 }}>Afficher :</label>
              <select
                style={styles.select}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(e.target.value === 'all' ? 'all' : parseInt(e.target.value));
                }}
                disabled={loading}
              >
                {generatePageSizeOptions().map((size, idx) => (
                  <option key={idx} value={size}>
                    {size === 'all' ? 'Tous afficher' : `${size} lignes`}
                  </option>
                ))}
              </select>
            </div>

            {(user?.is_super_admin || user?.permissions?.includes("EXPORT_PDF")) && (
              <div>
                <button
                  className="btn btn-danger"
                  onClick={handleExportPDF}
                  disabled={loading}
                  style={{ minWidth: '160px', padding: '8px 14px', fontSize: '1rem' }}
                >
                  <FaDownload className="me-2" />
                  {loading ? 'Génération du PDF...' : 'Exporter PDF'}
                </button>
              </div>
            )}
          </div>

          {/* Tableau avec détails des primes */}
          <div className="card shadow-sm mb-4" style={{ borderRadius: '15px', border: 'none' }}>
            <div className="card-body table-responsive p-0">
              <table className="table table-hover text-center align-middle m-0">
                <thead>
                  <tr style={styles.tableHeader}>
                    <th>Nom</th>
                    <th>Prénom</th>
                    <th><FaUserCheck title="Présences" /></th>
                    <th><FaExclamationTriangle title="Retards" /></th>
                    <th><FaUserTimes title="Absences" /></th>
                    <th>Heures</th>
                    <th>Ponctualité</th>
                    <th>Heures</th>
                    <th>Innovation</th>
                    <th>Total Primes</th>
                    <th>Pénalités</th>
                    <th>Solde</th>
                    <th>Action</th>
                   </tr>
                </thead>
                <tbody>
                  {stats.length ? stats.map((s) => {
                    const primesInnovationEmploye = primesInnovation
                      .filter(p => p.auteur_nom === s.nom && p.auteur_prenom === s.prenom)
                      .reduce((sum, p) => sum + p.montant_total, 0);
                    const totalPrimesEmploye = s.montant_primes + (s.montant_heures_supplementaires || 0) + primesInnovationEmploye;

                    return (
                      <tr key={s.id} className="table-row-hover">
                        <td>{s.nom}</td>
                        <td>{s.prenom}</td>
                        <td><span className="badge bg-success rounded-pill">{s.nbr_presences}</span></td>
                        <td><span className="badge bg-warning rounded-pill">{s.nbr_retards}</span></td>
                        <td><span className="badge bg-danger rounded-pill">{s.nbr_absences}</span></td>
                        <td className="text-info fw-bold">{s.heures_supplementaires || 0}h</td>
                        <td className="text-success">{formatMoney(s.montant_primes || 0)}</td>
                        <td className="text-info">{formatMoney(s.montant_heures_supplementaires || 0)}</td>
                        <td className="text-warning">{formatMoney(primesInnovationEmploye)}</td>
                        <td className="text-success fw-bold">{formatMoney(totalPrimesEmploye)}</td>
                        <td className="text-danger">{formatMoney(s.montant_penalites)}</td>
                        <td style={getSoldeStyle(s.solde)}>
                          {s.solde !== 0 && (s.solde > 0 ? <FiArrowUp className="me-1" /> : <FiArrowDown className="me-1" />)}
                          {formatMoney(s.solde)}
                        </td>
                        <td>
                          {(user?.is_super_admin || user?.permissions?.includes("VIEW_EMPLOYEE_STATISTICS")) && (
                            <button
                              className="btn btn-primary btn-sm"
                              style={{ backgroundColor: '#1B019B' }}
                              onClick={() => window.location.href = `/employes/${s.id}`}
                            >
                              Voir la fiche
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan="13" className="text-muted py-4">
                        {loading ? 'Chargement en cours...' : 'Aucune statistique disponible.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="card-footer d-flex justify-content-center mt-3 bg-transparent border-top-0">
                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(1)}
                        disabled={loading}
                        style={page === 1 ? styles.paginationDisabled : styles.paginationButton}
                      >
                        Première
                      </button>
                    </li>
                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(page - 1)}
                        disabled={loading}
                        style={page === 1 ? styles.paginationDisabled : styles.paginationButton}
                      >
                        &laquo;
                      </button>
                    </li>

                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }

                      return (
                        <li key={pageNum} className={`page-item ${page === pageNum ? 'active' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(pageNum)}
                            disabled={loading}
                            style={page === pageNum ? { ...styles.paginationButton, ...styles.paginationActive } : styles.paginationButton}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    })}

                    <li className={`page-item ${page === pagination.totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(page + 1)}
                        disabled={loading}
                        style={page === pagination.totalPages ? styles.paginationDisabled : styles.paginationButton}
                      >
                        &raquo;
                      </button>
                    </li>
                    <li className={`page-item ${page === pagination.totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.totalPages)}
                        disabled={loading}
                        style={page === pagination.totalPages ? styles.paginationDisabled : styles.paginationButton}
                      >
                        Dernière
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="text-center py-5">
          <p className="text-muted">Sélectionnez une organisation pour commencer</p>
        </div>
      )}

      {/* Modal des détails de prime d'innovation */}
      <PrimeDetailsModal
        show={showPrimeDetails}
        onClose={() => setShowPrimeDetails(false)}
        prime={selectedPrime}
      />
    </div>
  );
};

export default RHStatsDetailPage;